import { BenefitDocumentType } from '@zorro/clients';
import {
  createBenefitDocument,
  showErrorNotification,
  useMonolithMutation,
} from '@zorro/web/ui-utils';

import { UploadableBenefitDocument } from './benefitDocument.utils';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  documentType: BenefitDocumentType;
  isSharedWithEmployee?: boolean;
  /** Names the documents in the failure notification, e.g. "proof of consent". */
  documentLabel: string;
};

type PersistResult = {
  documents: UploadableBenefitDocument[];
  isSuccess: boolean;
};

/**
 * Uploads the picked files and creates their rows, returning the list with the
 * files swapped for the rows so a second pass doesn't duplicate them.
 */
export const usePersistUploadedDocuments = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  documentType,
  isSharedWithEmployee = false,
  documentLabel,
}: Props) => {
  // No invalidation: this also runs mid-flow (on the EnrollConnect/deeplink
  // action), where a refetch would remount the modal and wipe the form state.
  const { mutate } = useMonolithMutation({
    method: 'benefitDocumentsControllerCreateBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
    displayErrorNotification: false,
  });

  return async (
    documents: UploadableBenefitDocument[]
  ): Promise<PersistResult> => {
    if (documents.length === 0) return { documents, isSuccess: true };

    const results = await Promise.allSettled(
      documents.map(async (document) => {
        if (!(document instanceof File)) return document;

        const pendingDocument = await createBenefitDocument(
          employeeId,
          employerId,
          document,
          onboardingPeriodId,
          isSharedWithEmployee,
          documentType
        );
        return mutate([pendingDocument]);
      })
    );

    // Keep the rows that were created even when others failed, so a retry
    // re-uploads only the failures instead of duplicating the whole list.
    const persistedDocuments = results.map((result, index) =>
      result.status === 'fulfilled' ? result.value : documents[index]
    );

    const failedFileNames = documents
      .filter((_, index) => results[index]?.status === 'rejected')
      .map((document) =>
        document instanceof File ? document.name : document.fileName
      );
    if (failedFileNames.length > 0) {
      showErrorNotification({
        message: `Failed to save the ${documentLabel}: ${failedFileNames.join(', ')}. Please try again.`,
      });
    }

    return {
      documents: persistedDocuments,
      isSuccess: failedFileNames.length === 0,
    };
  };
};
